import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  customSetCookie,
  getBannerPlayerRegCookies,
  getXDirectBannerCookies,
  customUTCString
} from '~/utils/deviceInfo'
import { getFingerprint } from '~/utils/fingerprint'
dayjs.extend(utc)
// State
export const state = () => ({
  language: [],
  isMobile: false,
  maintenance: {},
  supportCountry: [],
  exchangeRate: [],
  countries: [],
  displayRegisterModal: false,
  isInternational: false,
  exchangeRateLoading: true,
  exchangeRateByPlayerCurrency: {}
})

// Actions
export const actions = {
  // Get exchange rate
  async getExchangeRate({ commit }) {
    commit('setExchangeRateLoading', true)
    try {
      const response = await this.$axios.get('/exchange-rate/fetch-all')
      commit('setExchangeRate', response.data)
    } catch (error) {
    } finally {
      commit('setExchangeRateLoading', false)
    }
  },
  async getExchangeRateByPlayerCurrency({ commit }, params) {
    commit('setExchangeRateLoading', true)
    try {
      const response = await this.$axios.get('/exchange-rate', { params })
      commit('setExchangeRateByPlayerCurrency', response.data)
    } catch (error) {
    } finally {
      commit('setExchangeRateLoading', false)
    }
  },
  // Get support languages
  async getSupportCountry({ commit }) {
    try {
      const response = await this.$axios.get('/website-setting/support-country')
      const supportCountry = response.data
      commit('setSupportCountry', response.data)
      if (supportCountry.length) {
        this.commit('settings/setCurrency', supportCountry[0]?.currency)
      }
    } catch (error) {
      return true
    }
  },
  // Get languages
  async getLanguage({ commit, rootState, dispatch }, currency) {
    try {
      const response = await this.$axios.get(
        `/languages/currency/${rootState.settings.defaultCurrency}`
      )
      const data = response.data
      // Fallback mechanism: If the locale was not successfully set via the default API
      // and there are available language options, set the default language from the data.
      if (!this.$cookie.get('wl_locale') && data?.length > 0) {
        const getDefaultLang = data.find(el => el.is_default)
        if (getDefaultLang) {
          dispatch('base-settings/updateLocaleAndSetCookie', getDefaultLang.language, {
            root: true
          })
        }
      }
      commit('setLanguage', data)
    } catch (error) {}
  },
  // Check maintanance
  async fetchMaintenance({ commit, rootState }) {
    try {
      const response = await this.$axios.get(
        `/website-setting/website-maintenance/${rootState.settings.defaultCurrency}`
      )
      commit('setMaintenance', response.data)
      return response.data
    } catch (error) {}
  },
  // Set visit website
  async fetchVisitWebsite({ commit }) {
    // Fetch every Website Page Visit
    // Fetch when only Website is not login yet
    // Fetch when the x-banner-id and x-player-type is not existing in the cookie

    const existingXBannerId = this.$cookie.get('x-direct-banner-id')
    const existingXPlayerType = this.$cookie.get('x-player-type')
    if (this.$cookie.get('token') || (existingXBannerId && existingXPlayerType)) return
    const headers = {}
    if (process.client) {
      headers['x-device-fingerprint'] = getFingerprint()
    }
    try {
      const response = await this.$axios.get('/visit', {
        params: { hideToast: true },
        headers
      })
      const xBannerId = response.headers['x-direct-banner-id']
      const xPlayerType = response.headers['x-player-type']
      const xClickId = response.headers['x-click-id']
      const utcTime = customUTCString()
      if (xBannerId) {
        customSetCookie(`x-direct-banner-id`, xBannerId, 10)
        customSetCookie(`x-direct-banner-id-${xBannerId}`, `${xBannerId}|${utcTime}`, 10)
      }
      if (xPlayerType) customSetCookie(`x-player-type`, xPlayerType, 10)
      if (xClickId) customSetCookie('x-click-id', xClickId, 10)

      return true
    } catch (error) {
      return false
    }
  },
  async markAssociatedPlayer({ commit }) {
    /**
     * This request is used to mark the associated players linked with the banner.
     * route should only be called in the case if the browser have old player id linked with the banners before.
     * If there is no player id present in the browser then this route will not be called.
     */

    const existingXPlayerType = this.$cookie.get('x-player-type')
    const xDevicePlayerId = getBannerPlayerRegCookies()
    const xDirectBannerIds = getXDirectBannerCookies()
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    const xBannerIds = xDirectBannerIds
      .map(item => {
        return {
          ...item,
          record_created_at: dayjs(item.record_created_at).utc().format('YYYY-MM-DD HH:mm:ss')
        }
      })
      .filter(item => uuidRegex.test(item.id))
    // If the x-device-player-id and x-banner-ids are not present in the cookie then return

    if (!xDevicePlayerId || !xBannerIds || !existingXPlayerType) return
    if (xDevicePlayerId?.length === 0) return
    if (xBannerIds?.length === 0) return
    try {
      const data = {
        player_ids: xDevicePlayerId,
        // This banner array will have only one object as it confirmed by backend team - mekky (comment for future reference)
        banners: xBannerIds
      }
      if (process?.client) {
        data.fingerprint = getFingerprint()
      }
      await this.$axios.post('/visit/mark-associate-player', data, {
        params: { hideToast: true }
      })
      return true
    } catch (error) {
      return false
    }
  },
  whiteLabel({ commit }) {
    const checkIfInternational = true
    commit('whiteLabel', checkIfInternational)
  }
}

// Mutations
export const mutations = {
  setLanguage(state, data) {
    state.language = data
  },
  setIsMobile(state, data) {
    state.isMobile = data
  },
  setMaintenance(state, val) {
    state.maintenance = val
  },
  setSupportCountry(state, val) {
    state.supportCountry = val
  },
  setExchangeRate(state, val) {
    state.exchangeRate = val
  },
  setCountries(state, val) {
    state.countries = val
  },
  setDisplayRegisterModal(state, val) {
    state.displayRegisterModal = val
  },
  whiteLabel(state, val) {
    state.isInternational = val
  },
  setExchangeRateLoading(state, val) {
    state.exchangeRateLoading = val
  },
  setExchangeRateByPlayerCurrency(state, val) {
    state.exchangeRateByPlayerCurrency = val
  }
}

// Getters
export const getters = {
  language: state => state.language.filter(lng => lng.is_active),
  maintenance: state => state.maintenance,
  supportCountry: state => state.supportCountry,
  exchangeRate: state => {
    const displayBothRateAndReveredRate = []
    if (state.exchangeRate?.length > 0) {
      state.exchangeRate.forEach(el => {
        // push default currency
        if (el.is_show_lnd) {
          displayBothRateAndReveredRate.push(el)
        }

        // Swap currency and rates with type 2 only.
        if (el.is_show_lnd_reverse) {
          const { rate, from_currency: fCurrency, reversed_rate: rRate } = el
          const swapEl = {
            ...el,
            from_currency: el.to_currency,
            to_currency: fCurrency,
            rate: rRate,
            reversed_rate: rate
          }
          displayBothRateAndReveredRate.push(swapEl)
        }
      })
    }
    return displayBothRateAndReveredRate
  },
  exchangeRatesWithCurrency: state => currency => {
    const displayBothRateAndReveredRate = []
    if (state.exchangeRate?.length > 0) {
      const data = JSON.parse(JSON.stringify(state.exchangeRate))
      const filteredFromCurrency = data.filter(el => el.from_currency === currency)
      filteredFromCurrency.forEach(el => {
        // push default currency
        if (el.is_show_lnd) {
          displayBothRateAndReveredRate.push(el)
        }

        // Swap currency and rates with type 2 only.
        if (el.is_show_lnd_reverse) {
          const { rate, from_currency: fCurrency, reversed_rate: rRate } = el
          const swapEl = {
            ...el,
            from_currency: el.to_currency,
            to_currency: fCurrency,
            rate: rRate,
            reversed_rate: rate
          }
          displayBothRateAndReveredRate.push(swapEl)
        }
      })
    }
    return displayBothRateAndReveredRate
  },
  swapAllExchangeRate: state => {
    const displayBothRateAndReveredRate = []
    if (state.exchangeRate?.length > 0) {
      state.exchangeRate.forEach(el => {
        displayBothRateAndReveredRate.push(el)
        const { rate, from_currency: fCurrency, reversed_rate: rRate } = el
        const swapEl = {
          ...el,
          from_currency: el.to_currency,
          to_currency: fCurrency,
          rate: rRate,
          reversed_rate: rate
        }
        displayBothRateAndReveredRate.push(swapEl)
      })
    }
    return displayBothRateAndReveredRate
  },
  countries: state => state.countries
}
